/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  ActionFail,
  ActionRequest,
  ActionSuccess,
  AssetUtils,
  Ducks,
  Seconds,
  TvAsset,
  VisualScrubberApi,
  Vod,
  XError,
} from '@wit/vtv-x-foundation-lib';
import Constants from 'configs/Constants';
import VisualScrubberUtils from 'core/services/utils/VisualScrubberUtils';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as Player from 'store/ducks/player';
import { StoreState } from 'store/store';

export enum ActionType {
  INIT = 'app/visualScrubber/streamWatcher/INIT',
  RETRY = 'app/visualScrubber/streamWatcher/RETRY',
  SUCCESS = 'app/visualScrubber/streamWatcher/SUCCESS',
  FAIL = 'app/visualScrubber/streamWatcher/FAIL',
  CLEAR = 'app/visualScrubber/streamWatcher/CLEAR',
}

interface InitRequestMeta {
  asset: TvAsset | Vod;
  stream: Player.LicensedStream;
  isRetry: boolean;
  progress?: Seconds;
}

interface SuccessfullPayload {
  asset: TvAsset | Vod;
  stream: Player.LicensedStream;
  url: string;
  isRetry: boolean;
}

export type ActiveStream = SuccessfullPayload;

type Init = ActionRequest<ActionType.INIT, InitRequestMeta>;
type Retry = ActionRequest<ActionType.RETRY>;
type Success = ActionSuccess<ActionType.SUCCESS, SuccessfullPayload>;
type Fail = ActionFail<ActionType.FAIL>;
type Clear = ActionRequest<ActionType.CLEAR>;

export type Actions = Init | Success | Fail | Clear;

const Actions = {
  init: (meta: InitRequestMeta): Init => ({ type: ActionType.INIT, meta }),
  retry: (): Retry => ({ type: ActionType.RETRY }),
  success: (payload: SuccessfullPayload): Success => ({ type: ActionType.SUCCESS, payload }),
  fail: (error: XError): Fail => ({ type: ActionType.FAIL, error }),
  clear: (): Clear => ({ type: ActionType.CLEAR }),
};

export const ActionCreators = {
  retry: Actions.retry,
};

export const Selectors = {
  getActiveStreamData: (store: StoreState): ActiveStream | undefined => store.visualScrubber.activeStream.data,
};

function* watchRequestSaga(action: Player.PlayerActionTypes): SagaIterator {
  const isVisualScrubberOn: boolean = yield select(Ducks.Dms.Selectors.isVisualScrubberOn);
  const activeStream: ActiveStream | undefined = yield select(Selectors.getActiveStreamData);

  if (!isVisualScrubberOn || action.type !== Player.ActionType.WATCH_REQUEST) {
    return;
  }

  const { asset: nextAsset } = action.meta;
  if (!activeStream || activeStream.asset.id !== nextAsset.id) {
    yield put(Actions.clear());
  }
}

function* watchSuccessSaga(action: Player.PlayerActionTypes): SagaIterator {
  const isVisualScrubberOn: boolean = yield select(Ducks.Dms.Selectors.isVisualScrubberOn);
  if (isVisualScrubberOn && action.type === Player.ActionType.WATCH_SUCCESS) {
    const { currentAsset, currentStream, progress, isTrailerMode } = action.payload;
    if (
      currentAsset &&
      currentStream &&
      !isTrailerMode && // VS is not available for trailers
      (AssetUtils.isTvAsset(currentAsset) || AssetUtils.isVod(currentAsset))
    ) {
      yield put(Actions.init({ asset: currentAsset, stream: currentStream, isRetry: false, progress }));
    }
  }
}

function* setCurrentAsset(action: Player.PlayerActionTypes): SagaIterator {
  const isVisualScrubberOn: boolean = yield select(Ducks.Dms.Selectors.isVisualScrubberOn);

  if (!isVisualScrubberOn || action.type !== Player.ActionType.SET_CURRENT_ASSET) {
    return;
  }

  const activeStream: ActiveStream | undefined = yield select(Selectors.getActiveStreamData);
  const { currentAsset, currentStream = activeStream?.stream } = action.meta;

  if (currentStream && currentAsset && (AssetUtils.isTvAsset(currentAsset) || AssetUtils.isVod(currentAsset))) {
    yield put(Actions.init({ asset: currentAsset, stream: currentStream, isRetry: false, progress: 0 }));
  }
}

function* seekSuccessSaga(action: Player.PlayerActionTypes) {
  const isVisualScrubberOn: boolean = yield select(Ducks.Dms.Selectors.isVisualScrubberOn);
  if (isVisualScrubberOn && action.type === Player.ActionType.SEEK_SUCCESS) {
    const activeStream: ActiveStream | undefined = yield select(Selectors.getActiveStreamData);
    const { currentAsset, currentStream, progress } = action.payload;
    if (
      currentAsset &&
      (AssetUtils.isTvAsset(currentAsset) || AssetUtils.isVod(currentAsset)) &&
      currentStream &&
      (!activeStream || activeStream.asset.id !== currentAsset.id)
    ) {
      yield put(Actions.init({ asset: currentAsset, stream: currentStream, isRetry: false, progress }));
    }
  }
}

function* watchFail(action: Player.PlayerActionTypes) {
  if (action.type === Player.ActionType.WATCH_FAIL) {
    yield put(Actions.fail(action.error));
  }
}

function* retrySaga(): SagaIterator {
  const activeStream: ActiveStream | undefined = yield select(Selectors.getActiveStreamData);
  if (activeStream) {
    const { asset, stream, isRetry } = activeStream;
    if (isRetry) {
      // This means a 2nd retry, so we will clear the base data.
      yield put(Actions.clear());
    } else {
      yield put(Actions.init({ asset, stream, isRetry: true }));
    }
  }
}

function* init(action: Init): SagaIterator {
  try {
    const { asset, stream, isRetry, progress } = action.meta;

    const queryParams = VisualScrubberUtils.getUrlParameters(asset, progress || 0);
    if (!queryParams) {
      throw new Error('[visualScrubber] Fail to retrive the query parameters from asset');
    }

    let shouldRetry = false;
    do {
      const response: Response = yield call(
        VisualScrubberApi.request,
        stream.uri + queryParams,
        Constants.FIRST_REQUEST_TIMEOUT,
        shouldRetry // If true, in a HTTP error it will create an exception and exit the loop
      );

      const filteredUrl = VisualScrubberUtils.removeUrlParameters(response.url);
      if (response.ok) {
        const isVisualscrubberCache: boolean = yield select(Ducks.Dms.Selectors.isVisualscrubberCache);
        yield put(Actions.success({ asset, stream, url: isVisualscrubberCache ? filteredUrl : stream.uri, isRetry }));
      } else {
        shouldRetry = stream.uri !== filteredUrl;
      }
    } while (shouldRetry);
  } catch (e) {
    yield put(Actions.fail(XError.fromError(e)));
  }
}

export function* sagas() {
  yield all([
    takeLatest(Player.ActionType.WATCH_REQUEST, watchRequestSaga),
    takeLatest(Player.ActionType.WATCH_SUCCESS, watchSuccessSaga),
    takeLatest(Player.ActionType.WATCH_FAIL, watchFail),
    takeLatest(Player.ActionType.SET_CURRENT_ASSET, setCurrentAsset),
    takeLatest(Player.ActionType.SEEK_SUCCESS, seekSuccessSaga),
    takeLatest(ActionType.RETRY, retrySaga),
    takeEvery(ActionType.INIT, init),
  ]);
}
